<template>
  <v-dialog
    v-if="internalValue"
    class="edit-ports-popup"
    v-model="internalValue"
    :width="750"
    @click:outside="onEscapeClick"
    @keydown.esc="onEscapeClick"
  >
    <v-card class="popup-wrapper">
      <span class="escape-link d-inline-block" @click="onEscapeClick"> X {{ $t('newProject.esc') }} </span>
      <div v-if='!showSummary'>
        <div class="popup-title">
          {{ $t('configuration.edit.ports') }}
        </div>
        <div class='select-host'>
          <Select
            class-name="domain-select"
            v-model="selectedHost"
            :options="hostsOptions"
            :title="$t('configuration.suppress.ports.choose.host')"
            :placeholder="$t('select')"
            size="small"
          />
        </div>
        <configuration-item-header
          :url-count="selectedPorts.length"
          :selected-items="selectedPorts"
          :all-items-count="currentPortsList.length"
          :select-all-label="$t('configuration.infrastructure.assets.selected.ports')"
          @select-all="selectAllPorts"
        >
          <template slot="additional-buttons">
            <v-btn :disabled="!selectedPorts.length" class="enable-btn" @click.stop="enable(selectedPorts)">enable</v-btn>
            <v-btn :disabled="!selectedPorts.length" class="suppress-btn ml-2" @click.stop="suppress(selectedPorts)">suppress</v-btn>
          </template>
        </configuration-item-header>

        <div class="ports-table">
          <div class="ports-table__header">
            <div class="port">{{ $t('configuration.infrastructure.assets.port') }}</div>
            <div class="information">{{ $t('configuration.infrastructure.assets.information') }}</div>
            <div class="reference">{{ $t('configuration.infrastructure.assets.reference') }}</div>
            <div class="action"/>
          </div>
          <div
            class="ports-table__row"
            v-for="item in currentPortsList"
            :key="item.port"
          >
            <div class="port">
              <CustomCheckbox
                color="cold-marine"
                :value="selectedPorts.includes(item.port)" size="s"
                @input="selectPort(item.port)"
              />
              {{ item.port }}
            </div>
            <div class="information">
              {{ item.information }}
            </div>
            <div class="reference">
              {{ item.reference }}
            </div>
            <div class="action">
              <v-btn
                v-if="currentSuppressedState.includes(item.port)"
                class="enable-btn"
                :min-width="100"
                @click.stop="enable([item.port])"
              >
                enable
              </v-btn>
              <v-btn
                v-else
                :min-width="100"
                class="suppress-btn"
                @click.stop="suppress([item.port])"
              >
                suppress
              </v-btn>
            </div>
          </div>
        </div>
        <div class="actions d-flex">
          <v-spacer />
          <v-btn class="btn-primary" @click="onNextClick">
            {{ $t('next') }}
          </v-btn>
        </div>
      </div>
      <div v-else>
        <div class='popup-title'>
          {{ $t('configuration.suppress.ports.summary.title') }}
        </div>
        <div class='popup-sub-title'>
          {{ $t('configuration.suppress.ports.summary.sub.title') }}
        </div>
        <div class='summary-content'>
          <div v-for='(hostKey, index) in Object.keys(allSuppressedState)' :key='index' class='summary-row'>
            <div class='host-name'> {{ hostKey }} </div>
            <div class='suppressed-ports' v-for='(port, portIndex) in allSuppressedState[hostKey]' :key='port'>
              <span> {{ `${port}` }} </span>
              <span v-if='portIndex !== allSuppressedState[hostKey].length - 1'> {{ separator }}</span>
            </div>
          </div>
          <div class='no-changes' v-if='!Object.keys(allSuppressedState).length'>
            {{ $t('configuration.suppress.ports.summary.no.changed') }}
          </div>
        </div>
        <div class='actions d-flex'>
          <v-spacer />
          <v-btn class="btn-tertiary" @click="showSummary = false">
            {{ $t('back') }}
          </v-btn>
          <v-btn class="btn-primary" :disabled='!Object.keys(allSuppressedState).length' @click="onSaveClick">
            {{ $t('save') }}
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import {ADD_URL_CONFIGURATIONS} from '@/store/project/actionTypes';
import {CONFIGURATION} from '@/constants/configuration';
import ConfigurationItemHeader from '@/components/configuration/ConfigurationItemHeader.vue';
import CustomCheckbox from '@/components/common/CustomCheckbox.vue';
import Select from '@/components/common/Select.vue';
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';

export default {
  name: 'SuppressPortsPopup',
  mixins: [disableOuterScrollOnPopupVisible],
  components: {
    CustomCheckbox,
    ConfigurationItemHeader,
    Select,
  },
  props: {
    hosts: {
      type: Array,
      required: true,
      default: () => [],
    },
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      separator: ', ',
      selectedPorts: [],
      currentSuppressedState: [],
      allSuppressedState: {},
      selectedHost: this.hosts[0].configData,
      showSummary: false,
    };
  },
  mounted() {
    this.currentPortsList.forEach(({ port, isSuppressed }) => {
      if (isSuppressed) {
        this.currentSuppressedState.push(port);
      }
    });
  },
  watch: {
    selectedHost(newValue, oldValue) {
      if (this.currentSuppressedState.length) {
        this.allSuppressedState[oldValue] = this.currentSuppressedState;
      }
      this.selectedPorts = [];
      this.currentSuppressedState = Object.keys(this.allSuppressedState).includes(newValue) ? this.allSuppressedState[newValue] : [];
      this.currentPortsList.forEach(({ port, isSuppressed }) => {
        if (isSuppressed) {
          this.currentSuppressedState.push(port);
        }
      });
    },
    currentSuppressedState: {
      deep: true,
      handler() {
        if (this.currentSuppressedState.length) {
          this.allSuppressedState[this.selectedHost] = this.currentSuppressedState;
        } else if (Object.keys(this.allSuppressedState).includes(this.selectedHost)) {
          delete this.allSuppressedState[this.selectedHost];
        }
      },
    },
  },
  computed: {
    hostsMap() {
      const hostMap = {};
      this.hosts.forEach(host => {
        hostMap[host.configData] = host;
      });
      return hostMap;
    },
    currentHostData() {
      return this.hostsMap[this.selectedHost];
    },
    currentPortsList() {
      let isSuppressed = false;
      if (this.currentHostData?.metaData && this.currentHostData?.metaData.length > 0) {
        isSuppressed = this.currentHostData?.ports?.some(port => this.currentHostData?.metaData?.includes(port));
      }
      return this.currentHostData?.ports?.map(port => ({
        isSuppressed,
        port,
        information: '',
        reference: '',
      })) || [];
    },
    hostsOptions() {
      return this.hosts.map(host => host.configData) || [];
    },
    internalValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    metaData() {
      return this.currentPortsList.filter(item => item.isSuppressed).map(item => item.port);
    },
  },
  methods: {
    async onSaveClick() {
      const payload = [];
      Object.keys(this.allSuppressedState).forEach(hostKey => {
        payload.push({
          data: hostKey,
          metaData: [...this.allSuppressedState[hostKey]],
          tld: this.hostsMap[hostKey].tld || '',
          subdomain: this.hostsMap[hostKey].subdomain || '',
        });
      });
      await this.$store.dispatch(ADD_URL_CONFIGURATIONS, {
        configType: CONFIGURATION.CONFIG_TYPES.HOST,
        payload,
      });
      this.onEscapeClick();
    },
    onNextClick() {
      if (this.currentSuppressedState.length) {
        this.allSuppressedState[this.selectedHost] = this.currentSuppressedState;
      }
      this.showSummary = true;
    },
    onEscapeClick() {
      this.internalValue = false;
    },
    selectPort(port) {
      if (this.selectedPorts.includes(port)) {
        this.selectedPorts = this.selectedPorts.filter(_port => _port !== port);
      } else {
        this.selectedPorts.push(port);
      }
    },
    selectAllPorts() {
      if (this.selectedPorts.length !== this.currentPortsList.length) {
        this.selectedPorts = this.currentPortsList.map(({ port }) => port);
      } else {
        this.selectedPorts = [];
      }
    },
    async suppress(ports) {
      if (ports.length) {
        this.currentSuppressedState.push(...ports);
      }
      this.currentSuppressedState = Array.from(new Set(this.currentSuppressedState));
    },
    enable(ports) {
      if (ports.length) {
        this.currentSuppressedState = this.currentSuppressedState.filter(port => !ports.includes(port));
      }
      this.currentSuppressedState = Array.from(new Set(this.currentSuppressedState));
    },
  },
};
</script>

<style lang="scss">
@import '~@/assets/styles/design-system';

.v-dialog {
  overflow-y: auto;
}
</style>
<style scoped lang="scss">
@import '~@/assets/styles/design-system';

.popup-wrapper {
  padding: rem(25) $space-10 rem(30);
  .summary-content {
    display: flex;
    flex-direction: column;
    .no-changes {
      font-size: $font-size-base;
      font-family: $font-family-regular;
      text-transform: capitalize;
      text-align: center;
      color: var(--text-secondary);
      font-weight: 500;
      margin-bottom: rem(7);
    }
    .summary-row {
      display: flex;
      margin-bottom: rem(10);
      .host-name {
        min-width: 30%;
        font-family: $font-family-regular;
        font-size: $font-size-base;
        letter-spacing: 1px;
        color: var(--text-secondary);
      }
      .suppressed-ports {
        .v-chip {
          display: initial;
          margin-right: $space-1-5;
          border: $border-width-thin solid var(--text-primary);
          background-color: transparent;
        }
      }
    }
  }
  .select-host {
    width: rem(200);
    padding-bottom: $space-5;
  }
  .escape-link {
    float: right;
    cursor: pointer;
    margin-left: $space-5;
    text-align: right;
    font-size: $font-size-sm;
    text-transform: uppercase;
    text-decoration: none;
    color: var(--brand-primary);
    font-family: $font-family-medium;
    font-weight: 500;
    letter-spacing: 1.1px;
  }
  .popup-title {
    font-size: $font-size-md;
    font-family: $font-family-medium;
    text-transform: uppercase;
    color: var(--text-primary);
    font-weight: 500;
    margin-bottom: rem(10);
  }
  .popup-sub-title {
    color: var(--severity-critical);
    font-size: $font-size-sm;
    letter-spacing: 0.8px;
    line-height: $font-size-xs;
    margin-bottom: $space-5;
  }

  .ports-table {
    &__header {
      display: flex;

      .port, .information, .reference, .action {
        flex: 1;

        color: var(--text-secondary);
        font-family: $font-family-regular;
        font-size: $font-size-xs;
        letter-spacing: 0.8px;
        line-height: rem(18);
        text-transform: uppercase;

        &.port, &.action {
          max-width: rem(100);
        }
      }
    }

    &__row {
      border-bottom: $border-width-thin solid var(--border-primary);
      display: flex;
      padding: rem(10) $space-0;

      .checkbox-container {
        margin-top: rem(-5);
      }

      .port, .information, .reference, .action {
        align-items: center;
        display: flex;
        flex: 1;

        color: var(--text-primary);
        font-family: $font-family-regular;
        font-size: $font-size-base;
        letter-spacing: 0;
        line-height: $space-4;

        &.port, &.action {
          max-width: rem(100);
        }

        &.port {
          display: flex;
        }

        &.reference {
          color: var(--brand-primary);
        }
      }
    }
  }

  .actions {
    margin-top: $space-8;
  }

  .actions .v-btn {
    margin-left: $space-2;
  }

  // Nudge caret down to visually center it within the small input
  ::v-deep .select-host .el-select__caret {
    position: relative;
    top: 4px;
  }
}
</style>
